import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, IconButton, Tooltip, Chip, Link,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, Avatar, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Add as AddIcon, Send as SendIcon, Schedule as ScheduleIcon,
  Cancel as CancelIcon, OpenInNew as OpenInNewIcon, Image as ImageIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

type Status = 'requested' | 'in_design' | 'ready' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';

const STATUS_COLOR: Record<Status, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  requested: 'default', in_design: 'warning', ready: 'info', scheduled: 'primary',
  publishing: 'warning', published: 'success', failed: 'error', cancelled: 'default',
};
const STATUS_LABEL: Record<Status, string> = {
  requested: 'Solicitado', in_design: 'En diseño', ready: 'Listo', scheduled: 'Agendado',
  publishing: 'Publicando', published: 'Publicado', failed: 'Falló', cancelled: 'Cancelado',
};
const DESTINOS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
];
const TYPES = [
  { value: 'image', label: 'Imagen' },
  { value: 'carousel', label: 'Carrusel' },
  { value: 'reel', label: 'Reel' },
  { value: 'video', label: 'Video' },
  { value: 'story', label: 'Story' },
];

const fmtDate = (d?: string) => d ? new Date(d).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

interface Post {
  _id: string; channel: string; type: string; caption: string; mediaUrls: string[];
  status: Status; scheduledFor?: string; publishedAt?: string; meta?: { permalink?: string };
  result?: { error?: string }; createdAt?: string;
}

const emptyForm = { destinos: ['instagram', 'facebook'] as string[], type: 'image', caption: '', mediaUrls: [] as string[], scheduledFor: '' };

const ContentCalendarPage: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.getSocialPosts();
      setPosts((res.data as Post[]) || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Error cargando posts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true); setError(null);
    try {
      const res = await apiService.uploadSocialMedia(Array.from(files));
      const urls = (res.data?.urls as string[]) || [];
      setForm((f) => ({ ...f, mediaUrls: [...f.mediaUrls, ...urls] }));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error subiendo imagen');
    } finally { setUploading(false); }
  };

  const createPost = async (publishNow: boolean) => {
    setSaving(true); setError(null);
    try {
      const channels = form.destinos.map((d) => (form.type === 'story' ? `${d}_story` : d));
      const body: any = {
        channels, type: form.type, caption: form.caption, mediaUrls: form.mediaUrls,
        status: publishNow ? 'ready' : 'scheduled',
      };
      if (!publishNow && form.scheduledFor) body.scheduledFor = new Date(form.scheduledFor).toISOString();
      const res = await apiService.createSocialPost(body);
      const created = res.data as Post | Post[];
      const list = Array.isArray(created) ? created : (created ? [created] : []);
      if (publishNow) {
        for (const c of list) { if (c?._id) await apiService.publishSocialPostNow(c._id); }
      }
      setOpen(false); setForm(emptyForm);
      await fetchPosts();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error creando el post');
    } finally { setSaving(false); }
  };

  const publishNow = async (id: string) => {
    setBusyId(id); setError(null);
    try { await apiService.publishSocialPostNow(id); await fetchPosts(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Error al publicar'); }
    finally { setBusyId(null); }
  };

  const cancel = async (id: string) => {
    setBusyId(id);
    try { await apiService.deleteSocialPost(id); await fetchPosts(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Error al cancelar'); }
    finally { setBusyId(null); }
  };

  const canSchedule = form.mediaUrls.length > 0 && !!form.scheduledFor && form.destinos.length > 0;
  const canPublish = form.mediaUrls.length > 0 && form.destinos.length > 0;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <ScheduleIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>Contenido / Agenda</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Nuevo post</Button>
        <Tooltip title="Refrescar"><IconButton onClick={fetchPosts}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Media</TableCell>
                <TableCell>Caption</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Programado</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay posts todavía. Crea el primero con "Nuevo post".
                </TableCell></TableRow>
              )}
              {posts.map((p) => (
                <TableRow key={p._id} hover>
                  <TableCell>
                    <Avatar variant="rounded" src={p.mediaUrls?.[0]} sx={{ width: 44, height: 44 }}>
                      <ImageIcon fontSize="small" />
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={p.caption}>{p.caption || '—'}</Typography>
                    {p.status === 'failed' && p.result?.error && (
                      <Typography variant="caption" color="error" noWrap>{p.result.error}</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption">{p.channel}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmtDate(p.scheduledFor || p.publishedAt || p.createdAt)}</Typography></TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLOR[p.status] || 'default'} label={STATUS_LABEL[p.status] || p.status} />
                  </TableCell>
                  <TableCell align="right">
                    {p.meta?.permalink && (
                      <Tooltip title="Ver publicación"><IconButton size="small" component={Link} href={p.meta.permalink} target="_blank"><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {['requested', 'in_design', 'ready', 'scheduled', 'failed'].includes(p.status) && (
                      <>
                        <Tooltip title="Publicar ahora">
                          <span><IconButton size="small" color="primary" disabled={busyId === p._id} onClick={() => publishNow(p._id)}>
                            {busyId === p._id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title="Cancelar"><span><IconButton size="small" disabled={busyId === p._id} onClick={() => cancel(p._id)}><CancelIcon fontSize="small" /></IconButton></span></Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Compose dialog */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo post</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Publicar en (una vez → ambos)</Typography>
              <ToggleButtonGroup
                value={form.destinos} size="small" sx={{ display: 'flex', mt: 0.5 }}
                onChange={(_, v: string[]) => { if (v.length) setForm({ ...form, destinos: v }); }}
              >
                {DESTINOS.map((d) => <ToggleButton key={d.value} value={d.value} sx={{ flex: 1 }}>{d.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Box>
            <TextField select fullWidth label="Tipo" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            {form.type === 'story' && (
              <Alert severity="info" sx={{ py: 0 }}>El estado/historia sale en {form.destinos.map((d) => (d === 'instagram' ? 'Instagram' : 'Facebook')).join(' + ')}. El Estado de WhatsApp no se publica por API oficial.</Alert>
            )}
            <TextField label="Caption" multiline minRows={3} fullWidth value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
            <Box>
              <Button variant="outlined" component="label" startIcon={uploading ? <CircularProgress size={16} /> : <ImageIcon />} disabled={uploading}>
                Subir imagen(es)
                <input hidden type="file" accept="image/*" multiple onChange={(e) => handleUpload(e.target.files)} />
              </Button>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {form.mediaUrls.map((u, i) => <Avatar key={i} variant="rounded" src={u} sx={{ width: 56, height: 56 }} />)}
              </Stack>
            </Box>
            <TextField
              label="Agendar para (opcional)" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }}
              value={form.scheduledFor} onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cerrar</Button>
          <Button onClick={() => createPost(false)} disabled={!canSchedule || saving} startIcon={<ScheduleIcon />}>Agendar</Button>
          <Button variant="contained" onClick={() => createPost(true)} disabled={!canPublish || saving} startIcon={saving ? <CircularProgress size={16} /> : <SendIcon />}>Publicar ahora</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentCalendarPage;
